<template>
    <div class="engagement-info m-b-spacer-md">
        <div class="engagement-info__controls alignwide">
            <div class="radio-tray radio-tray--view">
                <label>
                    <input type="radio" name="show-table-view" :value="false" v-model="isTableView" @change="handleViewClick">
                    <span class="visual-select"></span>
                    <span class="radio-value">
                        <svg width="20" height="20" viewBox="0 0 20 20" class="m-r-spacer-xs"><use xlink:href="#icon--card-view"></use></svg>
                        Logos
                    </span>
                </label>
                <label>
                    <input type="radio" name="show-table-view" :value="true" v-model="isTableView" @change="handleViewClick">
                    <span class="visual-select"></span>
                    <span class="radio-value">
                        <svg width="22px" height="18px" viewBox="0 0 22 18" class="m-r-spacer-xs"><use xlink:href="#icon--list-view"></use></svg>
                        List
                    </span>
                </label>
            </div>
            <div class="radio-tray radio-tray--filter">
                <label v-show="isTableView">
                    <input type="radio" name="filter-orgs" :value="'none'" v-model="isFilteredBy" @click="handleFilterClick">
                    <span class="visual-select"></span>
                    <span class="radio-value">
                        All
                    </span>
                </label>
                <label>
                    <input type="radio" name="filter-orgs" :value="'members'" v-model="isFilteredBy" @click="handleFilterClick">
                    <span class="visual-select"></span>
                    <span class="radio-value">
                        Members
                    </span>
                </label>
                <label>
                    <input type="radio" name="filter-orgs" :value="'engaged'" v-model="isFilteredBy" @click="handleFilterClick">
                    <span class="visual-select"></span>
                    <span class="radio-value">
                        Engaged
                    </span>
                </label>
            </div>
        </div>
        <div v-for="section in groupedFilteredOrgs" :key="section.name">
            <div v-if="isTableView">
                <div v-if="showEngagementInfo(section.name)" class="engagement-info__section">
                    <h1 v-if="section.orgs.length > 0" class="has-text-align-center">{{ buildSectionTitle(section.name) }}</h1>
                    <whos-involved-app-section :orgs="section.orgs" :org-heading="buildColumnNameTitle(section.name)" :return-url="returnToUrl" display-type="table"></whos-involved-app-section>
                </div>
            </div>
            <div v-else class="engagement-info__section">
                <h1 class="has-text-align-center">{{ buildSectionTitle(section.name) }}</h1>
                <whos-involved-app-section :orgs="section.orgs" :return-url="returnToUrl" display-type="logos"></whos-involved-app-section>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import WhosInvolvedAppSection from './WhosInvolvedAppSection.vue'

const props = defineProps({
    orgs: Array,
})

const isTableView = ref(false)
const isFilteredBy = ref('none')

const filteredOrgs = ref([...props.orgs])
const groupedFilteredOrgs = ref([]);
const returnToUrl = ref(null);


onMounted(() => {
    getParamsAndSetControls()
    convertStringsToBooleans()
    filterAndSortOrgs()
})

watch(isFilteredBy, () => {
    filterAndSortOrgs()
})

// Setup 
function getParamsAndSetControls(){
    const urlParams = new URLSearchParams(window.location.search)
    const viewParam = urlParams.get('view')
    const filterParam = urlParams.get('filter')
    if (viewParam === 'table'){
        isTableView.value = true;
    } else {
        isFilteredBy.value = 'members'
    }
    if (filterParam === 'members' || filterParam === 'engaged' || filterParam === 'none') {
        isFilteredBy.value = filterParam
    }
    filterAndSortOrgs();
}

// Interactivity

function handleViewClick(){
    if(isFilteredBy.value === 'none'){
        isFilteredBy.value = 'members'
    }
    if(isTableView.value) {
        isFilteredBy.value = 'none'
    }
    updateUrlParams()
}
function handleFilterClick(){
    filterAndSortOrgs();
    updateUrlParams()
}

function updateUrlParams() {
    const queryParams = new URLSearchParams();
    setTimeout(() => {
        queryParams.set('view', isTableView.value ? 'table' : 'logos');
        queryParams.set('filter', isFilteredBy.value);
    
        const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${queryParams.toString()}`;
        window.history.pushState({ path: newUrl }, '', newUrl);        
        returnToUrl.value = window.location.href;
    }, 400);
}

// Filtering & Sorting

function convertStringsToBooleans() {
    props.orgs.forEach(org => {
        org.is_member = org.is_member === '1';
        org.list_as_engaged = org.list_as_engaged === '1';
    });
}


function filterAndSortOrgs() {
    filterOrgs()
    sortFilteredOrgs('name', 'desc')
    groupByType()
    returnToUrl.value = window.location.href;
}

function filterOrgs() {
    if (isFilteredBy.value === 'engaged') {
        filteredOrgs.value = props.orgs.filter(org => org.list_as_engaged);
    } else if (isFilteredBy.value === 'members') {
        filteredOrgs.value = props.orgs.filter(org => org.is_member);
    } else {
        filteredOrgs.value = [...props.orgs];
    }
}

function sortFilteredOrgs(method, order = 'asc') {
    const sortOrder = order === 'asc' ? 1 : -1

    if (method === 'name') {
        filteredOrgs.value.sort((a, b) => {
            const titleA = a.title || ''
            const titleB = b.title || ''
            return sortOrder * titleA.localeCompare(titleB)
        })
    } else if (method === 'list_as_engaged') {
        filteredOrgs.value.sort((a, b) => {
            if (a.list_as_engaged && !b.list_as_engaged) {
                return -1 * sortOrder
            }
            if (!a.list_as_engaged && b.list_as_engaged) {
                return 1 * sortOrder
            }
            return 0
        })
    }
}

function groupByType() {
    const orgs = filteredOrgs.value
    const groupedOrgs = {};

    orgs.forEach((org) => {
        org.type.forEach((type) => {
            if (!groupedOrgs[type]) {
                groupedOrgs[type] = [];
            }
            groupedOrgs[type].push(org);
        });
    });

    const result = [];

    for (const type in groupedOrgs) {
        result.push({
            name: type,
            orgs: groupedOrgs[type],
        });
    }

    const sortOrder = ['provider', 'supplier', 'sponsor', 'partner', 'collaborator'];

    result.sort((a, b) => {
        return sortOrder.indexOf(a.name) - sortOrder.indexOf(b.name);
    });

    groupedFilteredOrgs.value = result;
}

function buildSectionTitle(type){
    let prepend = ''
    let title = ''
    if(isFilteredBy.value === 'members') {
        prepend = 'Member '
    } else if(isFilteredBy.value === 'engaged') {
        prepend = 'Engaged '
    }
    
    switch (type) {
        case 'provider':
            title = 'Providers'
            break;
        case 'supplier':
            title = 'Suppliers'
            break
        case 'sponsor':
            title = 'Partners + Sponsors'
            break
        case 'partner':
            title = 'Partners'
            break
        case 'collaborator':
            title = 'Collaborators'
        default:
            break;
    }
    return prepend + title
    
}
function buildColumnNameTitle(type){
    let str = type
    if (str.endsWith("s")) {
        str = str.slice(0, -1) // remove the 's' at the end
    }
    str = str.charAt(0).toUpperCase() + str.slice(1)
    return 'Name of ' + str
}
function showEngagementInfo(sectionName){
    let shouldBeHidden = sectionName !== 'sponsor' && sectionName !== 'partner' && sectionName !== 'collaborator' ? true : false
    return shouldBeHidden
}

</script>

